import { colors } from '@client/new/components/colors';
import {
  Alert,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useRevenueView } from './useRevenueView';
import _ from 'lodash';
import { AsyncRenderer } from '@client/new/components/AsyncRenderer/AsyncRenderer';
import {
  RevenueSplitView,
  DistributionView,
  PayoutView,
  RevenueTableView,
} from './revenueMappers';
import { Key } from 'react';
import { PercentInput } from '@client/new/components/PercentInput/PercentInput';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { bgColorsVars } from '@design-system/vars.css';

type RevenueSplitProps = {
  influencerId: number;
};

export const SubscriptionRevenue = (p: RevenueSplitProps) => {
  const revenueModel = useRevenueView({ influencerId: p.influencerId });

  return (
    <div>
      <div className="flex flex-col mb-5">
        <Typography variant="headline" size="small">
          Subscription Revenue
        </Typography>
      </div>
      <AsyncRenderer
        data={revenueModel}
        loadRender={
          <>
            <Skeleton
              variant="rectangular"
              sx={{ width: '100%', height: 30, marginBottom: '10px' }}
            />
            <Skeleton
              variant="rectangular"
              sx={{ width: '100%', height: 90 }}
            />
          </>
        }
        renderer={(p) => (
          <div>
            <div className="flex flex-col gap-[30px]">
              <RevenueSplit
                {...p.revenueSplitView}
                showInfoAlert={p.showInfoAlert}
              />
              {!_.isEmpty(p.distributionView.rows) && (
                <RevenueSchedule {...p.distributionView} />
              )}
              {!_.isEmpty(p.payoutView.rows) && (
                <RevenueCustom {...p.payoutView} />
              )}
            </div>
            <Alert
              severity="info"
              sx={{
                marginTop: '20px',
                backgroundColor: 'rgb(194, 210, 236, 0.6)',
                color: 'black',
              }}
            >
              <Typography>
                {_.isEmpty(p.payoutView.rows)
                  ? 'If you wish to customize the Payout Schedule please contact the engineering team.'
                  : 'If you wish to customize the Payout Schedule or Hurdles & Guarantees, please contact the engineering team.'}
              </Typography>
            </Alert>
          </div>
        )}
      />
    </div>
  );
};

function RevenueRuleTable<T>({
  title,
  rows,
  header,
  cta,
  tableSx = {},
}: { title: string } & RevenueTableView<T>) {
  return (
    <>
      <div className="flex justify-between">
        <div className="flex flex-col mb-5">
          <Typography variant="body" size="large">
            {title}
          </Typography>
        </div>
        {!cta ? null : (
          <div className="flex flex-end gap-2.5 mb-2">
            {cta.map((item) => (
              <Button key={item.label} color="neutral" onClick={item.onClick}>
                {item.label}
              </Button>
            ))}
          </div>
        )}
      </div>
      <Table
        sx={{
          minWidth: 650,
          boxShadow:
            '0px 1px 2px 0px rgba(29, 41, 57, 0.08), 0px 0px 1px 0px #EAECF0',
          borderColor: 'transparent',
          borderRadius: '10px',
          ...tableSx,
        }}
      >
        <TableHead
          sx={{
            borderRadius: '10px',
            backgroundColor: bgColorsVars['N-20'],
            ...header?.sx,
          }}
        >
          <TableRow>
            {header.cells.map((cell) => (
              <TableCell
                key={cell.key as Key}
                align="left"
                className={`px-4 py-2 w-[${cell.width}] text-black font-semibold`}
                sx={{
                  '&:first-of-type': { borderRadius: '10px 0 0 0' },
                  '&:last-of-type': { borderRadius: ' 0 10px 0 0' },
                }}
              >
                <Typography variant="body" size="large">
                  {cell.label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={index}
              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
              {row.map(({ value, input }, i) => (
                <TableCell key={i} align="left" className="p-4">
                  {input ? (
                    <PercentInput
                      value={input.value}
                      onChange={input.onChange}
                      disabled={input.disabled}
                    />
                  ) : (
                    <Typography className="font-normal">{value}</Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
}

export const RevenueSplit = ({
  showInfoAlert,
  ...p
}: RevenueSplitView & { showInfoAlert: boolean }) => {
  return (
    <div>
      <RevenueRuleTable title="Revenue Split" {...p} />
      {showInfoAlert ? (
        <Alert
          severity="info"
          sx={{
            marginTop: '20px',
            backgroundColor: 'rgb(194, 210, 236, 0.6)',
            color: 'black',
          }}
        >
          <Typography>
            If you wish to customize Engagement fields please contact
            engineering team
          </Typography>
        </Alert>
      ) : null}
    </div>
  );
};

export const RevenueSchedule = (p: DistributionView) => {
  return (
    <div>
      <RevenueRuleTable title="Payout Schedule" {...p} />
    </div>
  );
};

export const RevenueCustom = (p: PayoutView) => {
  return (
    <div>
      <RevenueRuleTable title="Hurdles & Guarantees" {...p} />
    </div>
  );
};
